import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { paymentsApi, companyFeaturesApi } from "@/services/api";
import {
  Send,
  Search,
  MoreHorizontal,
  Eye,
  Loader2,
  RefreshCw,
  Plus,
  Clock,
  XCircle,
  Users,
} from "lucide-react";
import { formatDate, cn } from "@/lib/utils";

const statusConfig: Record<
  string,
  { label: string; variant: "default" | "secondary" | "success" | "warning" | "destructive" }
> = {
  DRAFT: { label: "Rascunho", variant: "secondary" },
  PENDING_APPROVAL: { label: "Aguardando Aprovação", variant: "warning" },
  APPROVED: { label: "Aprovado", variant: "success" },
  PROCESSING: { label: "Processando", variant: "default" },
  AWAITING_DEPOSIT: { label: "Aguardando Depósito", variant: "warning" },
  DEPOSITED: { label: "Depositado", variant: "default" },
  COMPLETED: { label: "Concluído", variant: "success" },
  FAILED: { label: "Falha", variant: "destructive" },
  CANCELLED: { label: "Cancelado", variant: "secondary" },
  REJECTED: { label: "Rejeitado", variant: "destructive" },
};

const railLabels: Record<string, string> = {
  wire: "Wire Transfer",
  sepa: "SEPA",
  spei: "SPEI",
};

function formatFiatAmount(value: string | number | undefined, currency: string): string {
  if (!value) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";

  const localeMap: Record<string, string> = { USD: "en-US", EUR: "de-DE", MXN: "es-MX" };
  const locale = localeMap[currency] || "en-US";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency || "USD",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatUsdc(value: string | number | undefined): string {
  if (!value) return "0.00 USDC";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "0.00 USDC";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num) + " USDC";
}

export function PagamentosPage() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Verify feature access
  const { data: enabledFeatures, isLoading: loadingFeatures } = useQuery({
    queryKey: ["company-features"],
    queryFn: () => companyFeaturesApi.getEnabled(),
    staleTime: 5 * 60 * 1000,
  });

  const hasAccess = enabledFeatures?.includes("INTERNATIONAL_PAYMENTS");

  useEffect(() => {
    if (!loadingFeatures && enabledFeatures && !hasAccess) {
      navigate("/dashboard", { replace: true });
    }
  }, [loadingFeatures, enabledFeatures, hasAccess, navigate]);

  // Fetch payments
  const {
    data: paymentsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["payments", currentPage, pageSize, statusFilter, currencyFilter, searchTerm],
    queryFn: () =>
      paymentsApi.listPayments({
        page: currentPage,
        limit: pageSize,
        status: statusFilter,
        currency: currencyFilter,
        search: searchTerm,
      }),
    enabled: hasAccess === true,
  });

  const payments = paymentsData?.items || [];
  const total = paymentsData?.total || 0;
  const totalPages = Math.ceil(total / pageSize);

  // Stats
  const stats = useMemo(() => {
    const pending = payments.filter(
      (p) => p.status === "PENDING_APPROVAL" || p.status === "APPROVED"
    ).length;
    const processing = payments.filter(
      (p) =>
        p.status === "PROCESSING" ||
        p.status === "AWAITING_DEPOSIT" ||
        p.status === "DEPOSITED"
    ).length;
    return { total, pending, processing };
  }, [payments, total]);

  const hasActiveFilters =
    statusFilter !== "all" || currencyFilter !== "all" || searchTerm !== "";

  if (loadingFeatures) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Verificando acesso...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Send className="h-5 w-5 sm:h-6 sm:w-6" />}
        title="Pagamentos Internacionais"
        description="Envie pagamentos para fornecedores no exterior"
        stats={[
          { value: stats.total, label: "Total" },
          { value: stats.pending, label: "Pendentes" },
          { value: stats.processing, label: "Processando" },
        ]}
        actions={
          <div className="flex gap-2">
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw className={cn("w-4 h-4 mr-2", isLoading && "animate-spin")} />
              Atualizar
            </Button>
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => navigate("/pagamentos/beneficiarios")}
            >
              <Users className="w-4 h-4 mr-2" />
              Beneficiários
            </Button>
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => navigate("/pagamentos/novo")}
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Pagamento
            </Button>
          </div>
        }
      />

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total de Pagamentos
            </CardTitle>
            <Send className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">pagamentos registrados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pendentes
            </CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.pending}</p>
            <p className="text-xs text-muted-foreground">aguardando aprovação</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Em Processamento
            </CardTitle>
            <Loader2 className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.processing}</p>
            <p className="text-xs text-muted-foreground">sendo processados</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-end">
        <div className="flex-1 min-w-[250px]">
          <label className="text-sm font-medium mb-2 block">Buscar</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Código, fornecedor ou invoice..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="w-full sm:w-48">
          <label className="text-sm font-medium mb-2 block">Status</label>
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value);
              setCurrentPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(statusConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>
                  {config.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-48">
          <label className="text-sm font-medium mb-2 block">Moeda</label>
          <Select
            value={currencyFilter}
            onValueChange={(value) => {
              setCurrencyFilter(value);
              setCurrentPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="USD">USD (Dólar)</SelectItem>
              <SelectItem value="EUR">EUR (Euro)</SelectItem>
              <SelectItem value="MXN">MXN (Peso)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            onClick={() => {
              setStatusFilter("all");
              setCurrencyFilter("all");
              setSearchInput("");
              setSearchTerm("");
              setCurrentPage(1);
            }}
          >
            Limpar filtros
          </Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : payments.length === 0 ? (
            <div className="p-8 text-center">
              <Send className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                {hasActiveFilters
                  ? "Nenhum pagamento encontrado com os filtros aplicados"
                  : "Você ainda não tem pagamentos internacionais"}
              </p>
              {!hasActiveFilters && (
                <Button onClick={() => navigate("/pagamentos/novo")}>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Primeiro Pagamento
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border bg-muted/30">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium">Código</th>
                    <th className="p-4 font-medium hidden md:table-cell">Data</th>
                    <th className="p-4 font-medium">Fornecedor</th>
                    <th className="p-4 font-medium hidden sm:table-cell">Moeda</th>
                    <th className="p-4 font-medium">Valor</th>
                    <th className="p-4 font-medium">Status</th>
                    <th className="p-4 font-medium text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => {
                    const status = statusConfig[payment.status] || {
                      label: payment.status,
                      variant: "secondary" as const,
                    };
                    const supplierName =
                      payment.supplierName ||
                      payment.beneficiary?.beneficiaryName ||
                      payment.inlineBeneficiaryName ||
                      "-";

                    return (
                      <tr
                        key={payment.id}
                        className="border-b border-border last:border-0 hover:bg-muted/20 cursor-pointer"
                        onClick={() => navigate(`/pagamentos/${payment.id}`)}
                      >
                        <td className="p-4">
                          <span className="font-medium">{payment.paymentCode}</span>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <span className="text-sm text-muted-foreground">
                            {formatDate(payment.createdAt)}
                          </span>
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="font-medium">{supplierName}</p>
                            {payment.invoiceNumber && (
                              <p className="text-sm text-muted-foreground">
                                #{payment.invoiceNumber}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="p-4 hidden sm:table-cell">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{payment.currency}</span>
                            <span className="text-xs text-muted-foreground">
                              ({railLabels[payment.paymentRail] || payment.paymentRail})
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div>
                            <span className="font-semibold">
                              {formatFiatAmount(payment.amount, payment.currency)}
                            </span>
                            {payment.amountUsdc && (
                              <p className="text-xs text-muted-foreground">
                                {formatUsdc(payment.amountUsdc)}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant={status.variant}>{status.label}</Badge>
                        </td>
                        <td className="p-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/pagamentos/${payment.id}`);
                                }}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Ver Detalhes
                              </DropdownMenuItem>
                              {payment.status === "PENDING_APPROVAL" && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-red-600"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/pagamentos/${payment.id}`);
                                    }}
                                  >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Cancelar
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Página {currentPage} de {totalPages} ({total} pagamentos)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
